import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import {
  Button,
  ButtonGroup,
  Divider,
  FormControlLabel,
  Switch,
  TextField,
} from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import { map, trim, isArray, join, filter } from 'lodash';
import Swal from 'sweetalert2';
import bonusProductAPI from '../../middleware/bonusProductAPI';
import BonusQrCodeReader from './BonusQrCodeReader';
import { PrefixSerialOptions } from '../../data/common';
import DoneRoundedIcon from '@material-ui/icons/DoneRounded';
import ClearRoundedIcon from '@material-ui/icons/ClearRounded';
import ConfirmationNumberRoundedIcon from '@material-ui/icons/ConfirmationNumberRounded';
import AssignmentRoundedIcon from '@material-ui/icons/AssignmentRounded';

const useStyles = makeStyles((theme) => ({
  stepBlock: {
    display: 'flex',
    flexDirection: 'column',
    flexWrap: 'nowrap',
    alignItems: 'stretch',
    gap: theme.spacing(2),
  },
  stepTitle: {
    fontSize: '1.2rem',
    fontWeight: 'bold',
    marginBottom: theme.spacing(2),
  },
  stepTitleSubText: {
    fontSize: '0.9rem',
    color: theme.palette.text.secondary,
  },
  stepBtns: {
    display: 'flex',
    flexDirection: 'row',
    gap: theme.spacing(1),
    justifyContent: 'space-evenly',
    margin: theme.spacing(2, 0, 0),
  },
  subTitle: {
    fontSize: '1.1rem',
    fontWeight: '600',
    margin: theme.spacing(1, 0),
    color: '#8b8b8b',
    display: 'flex',
    flexWrap: 'nowrap',
    justifyContent: 'center',
    alignItems: 'center',
    gap: theme.spacing(1),
  },
  scanRsCounts: {
    display: 'flex',
    flexDirection: 'row',
    flexWrap: 'wrap',
    justifyContent: 'center',
    alignItems: 'stretch',
    gap: theme.spacing(2),
    backgroundColor: '#eee',
    padding: theme.spacing(2),
    borderRadius: theme.spacing(1),
  },
  scanRs: {
    flexGrow: 1,
    flexBasis: '100%',
    overflowY: 'auto',
    maxHeight: '200px',
  },
  scanRsContent: {
    margin: theme.spacing(1, 0),
    fontSize: '0.8rem',
    fontWeight: '500',
    lineHeight: '1.5',
  },
  scanRsSuccess: {
    padding: theme.spacing(1),
    margin: theme.spacing(1, 0),
    borderRadius: theme.spacing(0.5),
    backgroundColor: '#6b936b',
    color: '#fff',
    display: 'flex',
    flexDirection: 'row',
    flexWrap: 'wrap',
    justifyContent: 'flex-start',
    alignItems: 'center',
    gap: theme.spacing(1),
  },
  scanRsError: {
    padding: theme.spacing(1),
    margin: theme.spacing(1, 0),
    borderRadius: theme.spacing(0.5),
    backgroundColor: '#e35858',
    color: '#fff',
    display: 'flex',
    flexDirection: 'row',
    flexWrap: 'wrap',
    justifyContent: 'flex-start',
    alignItems: 'center',
    gap: theme.spacing(1),
  },
  block: {
    margin: theme.spacing(1, 0),
    textAlign: 'center',
  },
}));

const PaperLinkScanner = ({
  userId,
  productPackageId,
  isConsignment,
  onIsConsignmentChange,
  userPayingMemberId,
  isLoading,
  onLoadingChange,
}) => {
  const classes = useStyles();
  const [scanQRCode, setScanQRCode] = useState();
  const [scanGroupQRCode, setScanGroupQRCode] = useState();
  const [scanInputMode, setScanInputMode] = useState();
  const [startSerial, setStartSerial] = useState('');
  const [endSerial, setEndSerial] = useState('');
  const [prefixSerial, setPrefixSerial] = useState();
  const [scanRs, setScanRs] = useState([]);

  // QRCode 掃描處理
  useEffect(() => {
    if (scanQRCode) {
      onLoadingChange && onLoadingChange(true);

      bonusProductAPI
        .qrcodeLinkUser(
          scanQRCode,
          productPackageId,
          userId,
          isConsignment,
          userPayingMemberId,
        )
        .then(
          (rs) => {
            let newScanRs = [...scanRs];

            newScanRs.unshift({
              status: 'success',
              message: `成功歸戶 ${scanQRCode}`,
            });

            setScanRs(newScanRs);

            Swal.fire({
              toast: true,
              position: 'bottom',
              icon: 'success',
              title: `成功歸戶 ${scanQRCode}`,
              timer: 1000,
              showConfirmButton: false,
            });
          },
          (error) => {
            const errorMessage = isArray(error.messages)
              ? join(error.messages, '<br />')
              : error;
            let newScanRs = [...scanRs];

            newScanRs.unshift({
              status: 'error',
              message: `歸戶失敗 ${scanQRCode}`,
              error: errorMessage,
            });

            setScanRs(newScanRs);

            Swal.fire({
              toast: true,
              position: 'bottom',
              icon: 'error',
              title: `歸戶失敗 ${scanQRCode}`,
              timer: 1000,
              showConfirmButton: false,
            });
          },
        )
        .then(() => {
          onLoadingChange && onLoadingChange(false);
          setScanQRCode(undefined);
        });
    }
  }, [scanQRCode]);

  // Group QRCode 掃描處理
  useEffect(() => {
    if (scanGroupQRCode) {
      onLoadingChange && onLoadingChange(true);

      bonusProductAPI
        .groupQRCodeLinkUser(
          scanGroupQRCode,
          productPackageId,
          userId,
          isConsignment,
          userPayingMemberId,
        )
        .then(
          (rs) => {
            let newScanRs = [...scanRs];

            newScanRs.unshift({
              status: 'success',
              message: `打包成功歸戶 ${scanGroupQRCode}`,
            });

            setScanRs(newScanRs);

            Swal.fire({
              toast: true,
              position: 'bottom',
              icon: 'success',
              title: `打包成功歸戶 ${scanGroupQRCode}`,
              timer: 1000,
              showConfirmButton: false,
            });
          },
          (error) => {
            const errorMessage = isArray(error.messages)
              ? join(error.messages, '<br />')
              : error;
            let newScanRs = [...scanRs];

            newScanRs.unshift({
              status: 'error',
              message: `打包歸戶失敗 ${scanGroupQRCode}`,
              error: errorMessage,
            });

            setScanRs(newScanRs);

            Swal.fire({
              toast: true,
              position: 'bottom',
              icon: 'error',
              title: `打包歸戶失敗 ${scanGroupQRCode}`,
              timer: 1000,
              showConfirmButton: false,
            });
          },
        )
        .then(() => {
          onLoadingChange && onLoadingChange(false);
          setScanGroupQRCode(undefined);
        });
    }
  }, [scanGroupQRCode]);

  const handleClickPaperLink = () => {
    const numberTest = /^[0-9]+$/;

    if (PrefixSerialOptions.includes(prefixSerial) === false) {
      Swal.fire({
        title: '請選擇序號開頭',
        icon: 'warning',
      });

      return false;
    }

    if (numberTest.test(startSerial) === false) {
      Swal.fire({
        title: '請輸入序號，並使用數字',
        icon: 'warning',
      });

      return false;
    }

    if (trim(endSerial) !== '' && numberTest.test(endSerial) === false) {
      Swal.fire({
        title: '連號結尾序號，請輸入數字',
        icon: 'warning',
      });

      return false;
    }

    if (trim(endSerial) !== '' && parseInt(startSerial) > parseInt(endSerial)) {
      Swal.fire({
        title: '連號結尾序號，不能大於序號',
        icon: 'warning',
      });

      return false;
    }

    onLoadingChange && onLoadingChange(true);

    bonusProductAPI
      .paperLinkUser(
        prefixSerial + String(startSerial),
        trim(endSerial) !== '' ? prefixSerial + String(endSerial) : undefined,
        productPackageId,
        userId,
        isConsignment,
        userPayingMemberId,
      )
      .then(
        (rs) => {
          const serialText = `${prefixSerial}${startSerial}${
            trim(endSerial) !== '' ? '-' + endSerial : ''
          }`;
          let newScanRs = [...scanRs];

          newScanRs.unshift({
            status: 'success',
            message: `成功歸戶 ${serialText}`,
          });

          setScanRs(newScanRs);

          Swal.fire({
            toast: true,
            position: 'bottom',
            icon: 'success',
            title: `成功歸戶 ${serialText}`,
            timer: 1000,
            showConfirmButton: false,
          });
        },
        (error) => {
          const errorMessage = isArray(error.messages)
            ? join(error.messages, '<br />')
            : error;
          const serialText = `${prefixSerial}${startSerial}${
            trim(endSerial) !== '' ? '-' + endSerial : ''
          }`;
          let newScanRs = [...scanRs];

          newScanRs.unshift({
            status: 'error',
            message: `歸戶失敗 ${serialText}`,
            error: errorMessage,
          });

          setScanRs(newScanRs);

          Swal.fire({
            toast: true,
            position: 'bottom',
            icon: 'error',
            title: `歸戶失敗 ${serialText}`,
            timer: 1000,
            showConfirmButton: false,
          });
        },
      )
      .then(() => {
        onLoadingChange && onLoadingChange(false);
        setPrefixSerial(undefined);
        setStartSerial('');
        setEndSerial('');
      });
  };

  const handleChangeIsConsignment = (e) => {
    onIsConsignmentChange && onIsConsignmentChange(e.target.checked);
  };

  const handleChangeScanQRCode = (qrcodeData) => {
    if (qrcodeData) {
      setScanQRCode(qrcodeData);
    }
  };

  const handleChangeScanGroupQRCode = (qrcodeData) => {
    if (qrcodeData) {
      setScanGroupQRCode(qrcodeData);
    }
  };

  const handleChangeStartSerial = (e) => {
    setStartSerial(trim(e.target.value));
  };

  const handleChangeEndSerial = (e) => {
    setEndSerial(trim(e.target.value));
  };

  return (
    <div className={classes.stepBlock}>
      {scanInputMode === 'qrcode' && (
        <div className={classes.stepTitle}>
          請掃描 QRCode
          <div className={classes.stepTitleSubText}>
            可連續掃描，提示完成即代表該張紙本已歸戶完成
          </div>
        </div>
      )}

      {scanInputMode === 'serial' && (
        <div className={classes.stepTitle}>
          請選擇序號開頭，並輸入序號
          <div className={classes.stepTitleSubText}>
            連號時，可輸入開頭和結尾，系統會自動補齊中間的序號
          </div>
        </div>
      )}

      <div className={classes.block}>
        <FormControlLabel
          control={
            <Switch
              onChange={handleChangeIsConsignment}
              checked={isConsignment}
              disabled={isLoading}
              color="primary"
              style={{ margin: 'auto' }}
            />
          }
          label="設定代銷"
        />
      </div>

      <div>
        {!scanInputMode && (
          <div className={classes.stepBtns}>
            <Button
              variant="contained"
              color="primary"
              onClick={() => setScanInputMode('groupQRCode')}
              disabled={isLoading}
              size="large">
              掃描打包 QRCODE
            </Button>

            <Button
              variant="contained"
              color="primary"
              onClick={() => setScanInputMode('qrcode')}
              disabled={isLoading}
              size="large">
              掃描單一紙本 QRCODE
            </Button>

            <Button
              variant="contained"
              color="primary"
              onClick={() => setScanInputMode('serial')}
              disabled={isLoading}
              size="large">
              輸入 LSS 序號
            </Button>
          </div>
        )}

        {scanInputMode === 'groupQRCode' && (
          <BonusQrCodeReader
            hideTitle
            verifyQrcodeData={handleChangeScanGroupQRCode}
            isLoading={isLoading}
          />
        )}

        {scanInputMode === 'qrcode' && (
          <BonusQrCodeReader
            hideTitle
            verifyQrcodeData={handleChangeScanQRCode}
            isLoading={isLoading}
          />
        )}

        {scanInputMode === 'serial' && (
          <div className={classes.stepBlock}>
            <div>
              <ButtonGroup variant="text" color="primary">
                {map(PrefixSerialOptions, (prefixSerialOption) => {
                  return (
                    <Button
                      key={prefixSerialOption}
                      onClick={() => setPrefixSerial(prefixSerialOption)}
                      color={
                        prefixSerial === prefixSerialOption
                          ? 'primary'
                          : 'default'
                      }
                      variant={
                        prefixSerial === prefixSerialOption
                          ? 'contained'
                          : 'text'
                      }>
                      {prefixSerialOption}
                    </Button>
                  );
                })}
              </ButtonGroup>
            </div>

            <TextField
              label="序號"
              variant="standard"
              value={startSerial}
              onChange={handleChangeStartSerial}
              disabled={isLoading}
            />

            <TextField
              label="連號結尾序號"
              variant="standard"
              value={endSerial}
              onChange={handleChangeEndSerial}
              disabled={isLoading}
            />
          </div>
        )}

        {!!scanInputMode && (
          <div className={classes.stepBtns}>
            <Button
              variant="contained"
              color="default"
              onClick={() => setScanInputMode(undefined)}
              disabled={isLoading}
              fullWidth
              size="small">
              返回選擇
            </Button>

            {scanInputMode === 'serial' && (
              <Button
                variant="contained"
                color="primary"
                disabled={isLoading}
                fullWidth
                onClick={handleClickPaperLink}>
                紙本序號歸戶
              </Button>
            )}
          </div>
        )}
      </div>

      {scanRs && scanRs.length > 0 && (
        <div>
          <div className={classes.subTitle}>
            <AssignmentRoundedIcon />
            目前掃描結果
          </div>

          <div className={classes.scanRsCounts}>
            <div>
              <div>
                <ConfirmationNumberRoundedIcon />
              </div>
              <div>全部 {scanRs.length} 筆</div>
            </div>

            <Divider orientation="vertical" flexItem />

            <div>
              <div>
                <DoneRoundedIcon />
              </div>
              <div>
                成功 {filter(scanRs, (rs) => rs.status === 'success').length} 筆
              </div>
            </div>

            <Divider orientation="vertical" flexItem />

            <div>
              <div>
                <ClearRoundedIcon />
              </div>
              <div>
                失敗 {filter(scanRs, (rs) => rs.status === 'error').length} 筆
              </div>
            </div>

            <div className={classes.scanRs}>
              <Divider variant="middle" />

              <div className={classes.scanRsContent}>
                {scanRs.map((scan, index) => (
                  <div
                    key={index}
                    className={
                      scan.status === 'success'
                        ? classes.scanRsSuccess
                        : classes.scanRsError
                    }>
                    {scan.status === 'success' ? (
                      <DoneRoundedIcon />
                    ) : (
                      <ClearRoundedIcon />
                    )}

                    {scan.message}
                    {scan.error && <div>{scan.error}</div>}
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

PaperLinkScanner.propTypes = {
  userId: PropTypes.string.isRequired,
  productPackageId: PropTypes.string,
  isConsignment: PropTypes.bool.isRequired,
  onIsConsignmentChange: PropTypes.func,
  userPayingMemberId: PropTypes.string,
  isLoading: PropTypes.bool,
  onLoadingChange: PropTypes.func,
};

PaperLinkScanner.defaultProps = {
  productPackageId: undefined,
  userPayingMemberId: undefined,
  isLoading: false,
};

export default PaperLinkScanner;
